import { Fragment, useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { ErrorPanel } from "./ErrorPanel";
import { SectionHead } from "./SectionHead";

// The tenant registry review surface. One row, one decision (no select-all). Rows are BRAND facts
// the LLM drafts; the founder confirms or corrects each. The queue is SPLIT into three groups:
//  • Named brands — the main attention (recognised + genuinely-unknown named brands).
//  • Unnamed use — a generic use with no brand ("Nail Salon"); confirming local is the founder's
//    own inference (an unnamed independent operator), so these sit apart and are cleared in a pass.
//  • Not a tenant — roster artifacts (Vacant, Master Lease), FILTERED by default, count visible.
// No impact/rate hint before the decision: confirmation answers "what is this brand", full stop;
// the consequence shows up after, in the mark.
const CLASSES = ["national", "regional", "local", "unknown", "not_a_tenant"];
// The national / regional / local split is RETIRED from this page's display (founder ruling 30 Sep):
// it is gone from the tiles, the filter row and the row chip, which now shows the row's STATUS.
// It survives in the confirm/correct CONTROLS, and the founder has ruled that it stays there: those
// are dev-only, and read-only production never renders them at all.
const FILTERS = ["unknown", "not_a_tenant"];
const LABEL = { national: "national", regional: "regional", local: "local",
                unknown: "unknown", not_a_tenant: "not a tenant" };

export default function TenantRegistry() {
  const { authedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [hideConfirmed, setHideConfirmed] = useState(false);
  const [openHistory, setOpenHistory] = useState({});
  const [pending, setPending] = useState({});   // per-row in-flight (no optimistic UI)
  const [banner, setBanner] = useState(null);    // persistent, must-acknowledge (S-UI-1)
  const [filter, setFilter] = useState("all");   // classification filter for the sitting

  const load = useCallback(async () => {
    setErr("");
    try {
      const d = await authedFetch("/underwriter/tenant-registry");
      setData(d);
      return d;
    } catch (e) {
      setErr(e.detail || "Failed to load the tenant registry.");
      return null;
    }
  }, [authedFetch]);

  useEffect(() => { load(); }, [load]);

  const classify = async () => {
    setBusy(true); setNotice("");
    try {
      const r = await authedFetch("/underwriter/tenant-registry/classify", { method: "POST" });
      setNotice(r.classified
        ? `Classified ${r.classified} new brand${r.classified === 1 ? "" : "s"} (spend $${(r.spend_micros / 1e6).toFixed(3)}).`
        : r.message || "Nothing new to classify.");
      load();
    } catch (e) { setBanner({ kind: "error", text: e.detail || "Classification failed — nothing was written." }); }
    finally { setBusy(false); }
  };

  // S-UI-1: NO optimistic UI. The row's state renders only from the server's response; a spinner
  // shows while in flight; a 409 refetches and reports what CHANGED, and stays until acknowledged.
  const confirm = async (row, classification) => {
    setPending((p) => ({ ...p, [row.id]: true }));
    try {
      const res = await authedFetch(`/underwriter/tenant-registry/${row.id}/confirm`, {
        method: "POST", body: { classification, rev: row.rev },
      });
      const saved = res.row;
      setNotice(res.corrected
        ? `Corrected “${row.name}” → ${LABEL[saved.classification]} (server-confirmed).`
        : `Confirmed “${row.name}” as ${LABEL[saved.classification]} (server-confirmed).`);
      await load();
    } catch (e) {
      if (e.status === 409) {
        const d = await load();
        const fresh = d && d.rows.find((r) => r.id === row.id);
        const last = fresh && fresh.history && fresh.history[fresh.history.length - 1];
        const who = last ? ` by ${last.actor} at ${(last.at || "").slice(0, 19).replace("T", " ")}` : "";
        const nowReads = fresh
          ? `It now reads ${LABEL[fresh.classification]} (${fresh.provenance === "operator_confirmed" ? "confirmed" : "drafted"})${who}.`
          : "";
        setBanner({ kind: "stale", text:
          `“${row.name}” changed since you loaded it — your confirm was REJECTED and nothing was written. ${nowReads} Reload and review again before confirming.` });
      } else {
        setBanner({ kind: "error", text: `Confirm failed for “${row.name}” — nothing was written. ${e.detail || ""}`.trim() });
      }
    } finally {
      setPending((p) => { const n = { ...p }; delete n[row.id]; return n; });
    }
  };

  const toggleHistory = (id) => setOpenHistory((o) => ({ ...o, [id]: !o[id] }));

  if (err && !data) return <ErrorPanel message={err} onRetry={load} testid="registry-error" />;
  if (!data) return <div className="loading" data-testid="registry-loading">Loading tenant registry…</div>;

  const s = data.split || {};
  const g = data.groups || {};
  const confHide = (r) => !(hideConfirmed && r.provenance === "operator_confirmed");
  const passFilter = (r) => filter === "all" || r.classification === filter;
  const named = data.rows.filter((r) => r.group === "named" && confHide(r) && passFilter(r));
  const unnamed = data.rows.filter((r) => r.group === "unnamed_use" && confHide(r) && passFilter(r));
  const artifacts = data.rows.filter((r) => r.group === "not_a_tenant");
  const pct = data.review_count ? Math.round((data.reviewed / data.review_count) * 100) : 0;
  const remaining = data.review_count - data.reviewed;

  return (
    <div data-testid="tenant-registry-view">
      <SectionHead eyebrow="THE LIBRARIAN · TENANT REGISTRY" testid="reg"
        headline={data.count === 0
          ? "No brands in the registry."
          : `${remaining > 0 ? `${remaining} brand${remaining === 1 ? "" : "s"} left to confirm` : "Every brand is confirmed"}, out of ${data.review_count}.`}
        lede={data.read_only
          ? `Brand credit facts the Underwriter reads. Empty on production in Release 1, by ruling. Registry version ${data.registry_version}.`
          : `Brand credit facts the Underwriter reads. Only confirmed rows move a mark. Registry version ${data.registry_version}.`} />

      {banner && (
        <div className={`decision-banner banner-${banner.kind}`} data-testid="confirm-banner" role="alert">
          <span className="banner-text">{banner.text}</span>
          <button className="banner-ack" data-testid="confirm-banner-ack" onClick={() => setBanner(null)}>
            Acknowledge
          </button>
        </div>
      )}

      <div className="review-progress" data-testid="review-progress">
        <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
        <span className="progress-label">
          <strong data-testid="reviewed-count">{data.reviewed}</strong> of {data.review_count} brands reviewed
          {remaining > 0 && <span className="muted"> · {remaining} to go</span>}
        </span>
        {data.reviewed > 0 && (
          <button className="linkbtn" data-testid="toggle-hide-confirmed"
                  onClick={() => setHideConfirmed((v) => !v)}>
            {hideConfirmed ? "show confirmed" : "hide confirmed"}
          </button>
        )}
      </div>

      <div className="stat-row">
        <Stat label="unknown" value={s.unknown || 0} />
        <Stat label="not a tenant" value={s.not_a_tenant || 0} />
      </div>

      <div className="filter-bar" data-testid="classification-filter">
        <span className="filter-label">Show</span>
        {["all", ...FILTERS].map((c) => (
          <button key={c} data-testid={`filter-${c}`}
            className={`filter-btn ${filter === c ? "active" : ""}`}
            onClick={() => setFilter(c)}>
            {c === "all" ? "all" : LABEL[c]}
          </button>
        ))}
        {filter !== "all" && <span className="filter-hint">— nationals first, adversarially</span>}
      </div>

      <div className="registry-actions">
        {!data.read_only && (
          <button className="btn-primary" data-testid="classify-btn" onClick={classify} disabled={busy}>
            {busy ? "Classifying…" : "Classify new brands"}
          </button>
        )}
        {notice && <span className="notice" data-testid="registry-notice">{notice}</span>}
        {err && <span className="error-inline" data-testid="registry-inline-error">{err}</span>}
      </div>

      {data.count === 0 ? (
        <div className="empty-state" data-testid="registry-empty">
          <h2>No brands yet</h2>
          {!data.read_only && (
            <p>Run “Classify new brands” to draft the registry from the corpus rosters.</p>
          )}
        </div>
      ) : (
        <>
          <GroupTable title="Named brands" testid="group-named" rows={named}
            confirm={confirm} pending={pending} openHistory={openHistory} toggleHistory={toggleHistory}
            readOnly={data.read_only} />

          {unnamed.length > 0 && (
            <GroupTable title="Unnamed use" testid="group-unnamed"
              hint="A generic use with no brand — confirming local is your inference that an unnamed operator is independent."
              rows={unnamed} confirm={confirm} pending={pending} openHistory={openHistory} toggleHistory={toggleHistory}
            readOnly={data.read_only} />
          )}

          {g.not_a_tenant > 0 && (
            <div className="artifact-block">
              <button className="linkbtn" data-testid="toggle-artifacts" onClick={() => setShowArtifacts((v) => !v)}>
                {showArtifacts ? `Hide ${g.not_a_tenant} not-a-tenant` : `${g.not_a_tenant} not-a-tenant filtered — show`}
              </button>
              {showArtifacts && (
                <GroupTable title="Not a tenant" testid="group-artifacts" rows={artifacts}
                  confirm={confirm} pending={pending} openHistory={openHistory} toggleHistory={toggleHistory}
            readOnly={data.read_only} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GroupTable({ title, testid, hint, rows, confirm, pending, openHistory, toggleHistory, readOnly }) {
  return (
    <section className="group-section" data-testid={testid}>
      <div className="group-head">
        <h2>{title} <span className="group-count">{rows.length}</span></h2>
        {hint && <p className="group-hint">{hint}</p>}
      </div>
      <div className="reg-table-wrap">
        <table className="reg-table" data-testid={`${testid}-table`}>
          <thead>
            <tr>
              <th>Brand</th><th>Status</th><th>Firing</th>
              <th className="num">Props</th><th>Reason</th><th>Confirm / correct</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <Fragment key={row.id}>
                <tr data-testid={`registry-row-${row.id}`}
                    className={`${row.provenance === "operator_confirmed" ? "confirmed" : ""} ${row.classification === "not_a_tenant" ? "artifact" : ""}`}>
                  <td className="brand-cell">
                    <button className="brand-btn" data-testid={`history-toggle-${row.id}`}
                            title="Show correction history" onClick={() => toggleHistory(row.id)}>
                      {row.name}
                      {row.history && row.history.length > 0 && <span className="hist-dot">•</span>}
                    </button>
                  </td>
                  <td><span className="prov prov-neutral" data-testid={`prov-${row.id}`}>
                    {row.provenance === "operator_confirmed" ? "confirmed" : "drafted"}</span></td>
                  <td>{row.fires
                    ? <span className="fires-yes" data-testid={`fires-${row.id}`}>firing</span>
                    : <span className="fires-no">—</span>}</td>
                  <td className="num">{row.property_count}</td>
                  <td className="reason-cell" title={row.reason}>{row.reason}</td>
                  <td className="confirm-cell">
                    {readOnly ? (
                      <span className="muted" data-testid={`read-only-${row.id}`}>read-only</span>
                    ) : pending[row.id] ? (
                      <span className="row-saving" data-testid={`saving-${row.id}`}>saving…</span>
                    ) : CLASSES.map((c) => (
                      <button key={c} data-testid={`confirm-${row.id}-${c}`}
                        className={`confirm-btn ${c === row.classification ? "same" : "correct"}`}
                        disabled={!!pending[row.id]}
                        onClick={() => confirm(row, c)}>
                        {c === row.classification ? "confirm" : LABEL[c]}
                      </button>
                    ))}
                  </td>
                </tr>
                {openHistory[row.id] && (
                  <tr className="history-row" data-testid={`history-${row.id}`}>
                    <td colSpan={6}>
                      {row.history && row.history.length > 0 ? (
                        <ul className="history-list">
                          {row.history.map((h, i) => (
                            <li key={i}>
                              <span className="hist-when">{(h.at || "").slice(0, 19).replace("T", " ")}</span>
                              <span className="hist-actor">{h.actor}</span>
                              <span className="hist-change">
                                {h.action === "reset_test_confirmation"
                                  ? `reset → ${h.restored_classification}`
                                  : `${h.prior_classification} → ${h.new_classification || h.prior_classification}`}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : <span className="hist-empty">No corrections yet — still the original LLM draft.</span>}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`stat ${tone || ""}`} data-testid={`registry-stat-${label.replace(/ /g, "-")}`}>
      <div className="stat-val">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
